import { Node, mergeAttributes } from "@tiptap/core";
import { ReactNodeViewRenderer, NodeViewWrapper, NodeViewContent } from "@tiptap/react";
import React from "react";
import { useIntl } from "react-intl";
import Messages from "../../Shared/Messages";

/**
 * Editable section box — a clearly-marked "edit here" zone for the review editor
 * (and authored free-text sections in the designer). Used for:
 *  - kind "ai":   AI-generated output, wrapped at generation. Labelled so the
 *                 reviewer knows it's a draft to check/edit.
 *  - kind "free": a free-text section authored in the template; staff complete it.
 *
 * It's a content-bearing node (not an atom), so its body is naturally editable.
 * The label bar is non-editable chrome rendered by the node view only — it is
 * NOT part of the serialized HTML, so it never leaks into the generated PDF
 * (the design-system CSS doesn't style `.mps-editable-section`, so the box just
 * renders its content).
 */

const DEFAULT_LABELS = {
  ai: "AI draft — review & edit",
  free: "Free text box",
};
const ICONS = { ai: "fa-robot", free: "fa-pen" };

const EditableSectionComponent = ({ node, updateAttributes }) => {
  const intl = useIntl();
  const kind = node.attrs.kind || "free";
  const approved = kind === "ai" && !!node.attrs.approved;
  const label = node.attrs.label || DEFAULT_LABELS[kind] || "Edit";
  // Empty free-text sections render as a dashed "Click to fill" prompt.
  const isEmpty = (node.textContent || "").trim().length === 0;
  return (
    <NodeViewWrapper
      className={`mps-editable-section kind-${kind}${isEmpty ? " is-empty" : ""}${
        approved ? " is-approved" : ""
      }`}
    >
      <div className="es-label" contentEditable={false}>
        <i className={`fa ${ICONS[kind] || "fa-pen"}`}></i> {label}
        {kind === "ai" && (
          <button
            type="button"
            className={`es-approve${approved ? " approved" : ""}`}
            title={intl.formatMessage(Messages.doc_ai_approve_hint)}
            onClick={() => updateAttributes({ approved: !approved })}
          >
            <i className={`fa ${approved ? "fa-check-circle" : "fa-check"}`}></i>{" "}
            {intl.formatMessage(approved ? Messages.doc_ai_approved : Messages.doc_ai_approve)}
          </button>
        )}
      </div>
      <NodeViewContent className="es-body" />
    </NodeViewWrapper>
  );
};

export const EditableSection = Node.create({
  name: "editableSection",
  group: "block",
  content: "block+",
  defining: true,

  addAttributes() {
    return {
      kind: {
        default: "free",
        parseHTML: (el) => el.getAttribute("data-section-kind") || "free",
        renderHTML: (attrs) => ({ "data-section-kind": attrs.kind }),
      },
      label: {
        default: "",
        parseHTML: (el) => el.getAttribute("data-label") || "",
        renderHTML: (attrs) => (attrs.label ? { "data-label": attrs.label } : {}),
      },
      approved: {
        default: false,
        parseHTML: (el) => el.getAttribute("data-ai-approved") === "true",
        renderHTML: (attrs) => (attrs.approved ? { "data-ai-approved": "true" } : {}),
      },
    };
  },

  parseHTML() {
    return [{ tag: "div[data-editable-section]" }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      "div",
      mergeAttributes(HTMLAttributes, {
        "data-editable-section": "",
        class: "mps-editable-section",
      }),
      0,
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(EditableSectionComponent);
  },

  addCommands() {
    return {
      insertEditableSection:
        (attrs = {}) =>
        ({ commands }) =>
          commands.insertContent({
            type: this.name,
            attrs: { kind: "free", label: DEFAULT_LABELS.free, ...attrs },
            content: [{ type: "paragraph" }],
          }),
    };
  },
});

export default EditableSection;
